/**
 * IdeasResults.js
 * Netflix-style idea cards with score badges, save buttons, and personalization indicators.
 */
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
    Box, Paper, Typography, Chip, IconButton, Tooltip,
    alpha, Snackbar, useTheme, Divider,
} from '@mui/material';
import {
    ContentCopy as CopyIcon,
    CheckCircleOutline as CopiedIcon,
    TrendingUp as TrendingUpIcon,
    FormatQuote as QuoteIcon,
    RocketLaunch as RocketIcon,
    AutoAwesome as AutoAwesomeIcon,
    Bookmark as BookmarkIcon,
    BookmarkBorder as BookmarkBorderIcon,
    AccessTime as TimeIcon,
    YouTube as YouTubeIcon,
    Instagram as InstagramIcon,
    LinkedIn as LinkedInIcon,
    Twitter as TwitterIcon,
    Facebook as FacebookIcon,
    MusicNote as TikTokIcon,
    Language as AllPlatformsIcon,
    Explore as ExploreIcon,
    Whatshot as WhatshotIcon,
    Psychology as PsychologyIcon,
    EmojiObjects as LightbulbIcon,
    CheckCircle as BulletCheckIcon,
    Cloud as CloudIcon,
} from '@mui/icons-material';
import { FORMAT_COLORS, ENGAGEMENT_COLORS, PLATFORM_COLORS, IDEA_MODES, getScoreColor } from './constants';

// Platform icon map
const PLATFORM_ICONS = {
    YouTube:   YouTubeIcon,
    Instagram: InstagramIcon,
    LinkedIn:  LinkedInIcon,
    Twitter:   TwitterIcon,
    Facebook:  FacebookIcon,
    TikTok:    TikTokIcon,
};

const MODE_ICONS = {
    smart: AutoAwesomeIcon,
    fill_gap: ExploreIcon,
    double_down: WhatshotIcon,
    trending_you: TrendingUpIcon,
};

function PlatformChip({ platform }) {
    const color = PLATFORM_COLORS[platform] || '#6366f1';
    const Icon  = PLATFORM_ICONS[platform] || AllPlatformsIcon;
    return (
        <Chip
            icon={<Icon sx={{ fontSize: '12px !important', color: `${color} !important` }} />}
            label={platform}
            size="small"
            variant="outlined"
            sx={{
                height: 20,
                fontSize: '0.62rem',
                fontWeight: 700,
                borderRadius: '6px',
                borderColor: alpha(color, 0.3),
                color,
                '& .MuiChip-icon': { ml: '5px' },
            }}
        />
    );
}

function ScoreBadge({ score }) {
    if (score == null) return null;
    const color = getScoreColor(score);
    return (
        <Box sx={{
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: `2px solid ${color}`,
            bgcolor: alpha(color, 0.1),
        }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color, lineHeight: 1 }}>
                {score}
            </Typography>
        </Box>
    );
}

function IdeaCard({ idea, onCopy, onSave, copiedIdx, savedIds, index }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isCopied = copiedIdx === index;
    const isSaved = savedIds.has(idea.id);
    const formatColor = FORMAT_COLORS[idea.format] || '#6366f1';
    const engagementColor = ENGAGEMENT_COLORS[idea.engagement] || '#9ca3af';

    return (
        <Box sx={{
            borderRadius: '12px',
            border: `1px solid ${alpha(formatColor, isDark ? 0.2 : 0.15)}`,
            background: theme.palette.background.paper,
            borderLeft: `4px solid ${formatColor}`,
            transition: 'all 0.2s ease',
            overflow: 'hidden',
            '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 28px ${alpha(formatColor, isDark ? 0.25 : 0.12)}`,
                borderColor: alpha(formatColor, 0.4),
            },
        }}>
            {/* Card header */}
            <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* Score badge or numbered badge */}
                {idea.predicted_score != null
                    ? <ScoreBadge score={idea.predicted_score} />
                    : (
                        <Box sx={{
                            width: 34, height: 34, flexShrink: 0,
                            borderRadius: '8px',
                            bgcolor: alpha(formatColor, isDark ? 0.2 : 0.1),
                            color: formatColor,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 800, fontSize: '0.9rem',
                        }}>
                            {index + 1}
                        </Box>
                    )
                }

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.3, mb: 0.5 }}>
                        {idea.title}
                    </Typography>
                    {/* Score reason */}
                    {idea.score_reason && (
                        <Typography variant="caption" sx={{ color: getScoreColor(idea.predicted_score), fontWeight: 600, display: 'block', mb: 0.5 }}>
                            {idea.score_reason}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip label={idea.format} size="small" sx={{
                            bgcolor: alpha(formatColor, 0.1), color: formatColor,
                            fontWeight: 700, height: 20, fontSize: '0.65rem', borderRadius: '6px',
                        }} />
                        <Chip
                            icon={<TrendingUpIcon sx={{ fontSize: '0.75rem !important', color: `${engagementColor} !important` }} />}
                            label={`${idea.engagement} Engagement`} size="small"
                            sx={{
                                bgcolor: alpha(engagementColor, 0.08), color: engagementColor,
                                fontWeight: 700, height: 20, fontSize: '0.65rem', borderRadius: '6px',
                            }}
                        />
                        {(idea.platforms || []).map(p => (
                            <PlatformChip key={p} platform={p} />
                        ))}
                    </Box>
                </Box>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                    <Tooltip title={isSaved ? 'Saved' : 'Save idea'}>
                        <IconButton size="small" onClick={() => onSave(idea, index)}
                            sx={{ color: isSaved ? '#f59e0b' : 'text.disabled' }}>
                            {isSaved
                                ? <BookmarkIcon sx={{ fontSize: 20 }} />
                                : <BookmarkBorderIcon sx={{ fontSize: 20 }} />
                            }
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isCopied ? 'Copied!' : 'Copy idea'}>
                        <IconButton size="small" onClick={() => onCopy(idea, index)}
                            sx={{ color: isCopied ? '#10b981' : 'text.disabled' }}>
                            {isCopied ? <CopiedIcon sx={{ fontSize: 20 }} /> : <CopyIcon sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Divider sx={{ mx: 2.5, borderColor: alpha(formatColor, 0.1) }} />

            {/* Hook */}
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1.5 }}>
                <QuoteIcon sx={{ color: alpha(formatColor, isDark ? 0.3 : 0.2), fontSize: 24, mt: -0.5, flexShrink: 0 }} />
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', fontStyle: 'italic', lineHeight: 1.5 }}>
                    {idea.hook}
                </Typography>
            </Box>

            {/* Key Points — what to actually include/do */}
            {idea.key_points?.length > 0 && (
                <Box sx={{ mx: 2.5, mb: 1.5, p: 1.5, borderRadius: '8px', bgcolor: alpha(formatColor, isDark ? 0.07 : 0.04), border: `1px solid ${alpha(formatColor, 0.15)}` }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: formatColor, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                        What to include
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {idea.key_points.map((pt, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                <BulletCheckIcon sx={{ fontSize: 13, color: formatColor, mt: '2px', flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8rem', lineHeight: 1.5 }}>{pt}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Why it works */}
            {idea.why_it_works && (
                <Box sx={{ mx: 2.5, mb: 1.5, px: 1.5, py: 1, borderRadius: '8px', bgcolor: alpha('#10b981', isDark ? 0.08 : 0.05), border: `1px solid ${alpha('#10b981', 0.2)}`, display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                    <LightbulbIcon sx={{ fontSize: 14, color: '#10b981', mt: '2px', flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.78rem', lineHeight: 1.5 }}>
                        <Box component="span" sx={{ fontWeight: 700, color: '#10b981' }}>Why it works: </Box>
                        {idea.why_it_works}
                    </Typography>
                </Box>
            )}

            {/* Post copy / Body */}
            <Box sx={{
                mx: 2.5, mb: 1.5, p: 1.75,
                borderRadius: '8px',
                bgcolor: isDark ? alpha('#000', 0.2) : alpha(theme.palette.background.default, 0.8),
                border: `1px solid ${alpha(formatColor, 0.1)}`,
            }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.disabled', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                    Post copy
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-line', lineHeight: 1.65 }}>
                    {idea.body}
                </Typography>
                {idea.cta && (
                    <Box sx={{ mt: 1.25, pt: 1, borderTop: `1px dashed ${alpha(formatColor, 0.2)}` }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: formatColor, fontSize: '0.85rem' }}>
                            CTA: {idea.cta}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Footer — tags + best time */}
            <Box sx={{ px: 2.5, pb: 2, display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                {(idea.tags || []).slice(0, 5).map(t => (
                    <Typography key={t} variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                        #{t}
                    </Typography>
                ))}
                {idea.best_time && (
                    <Chip
                        icon={<TimeIcon sx={{ fontSize: '11px !important' }} />}
                        label={`Post: ${idea.best_time}`}
                        size="small"
                        sx={{
                            ml: 'auto', height: 18, fontSize: '0.58rem', fontWeight: 600,
                            bgcolor: alpha('#6366f1', 0.08), color: '#6366f1',
                            '& .MuiChip-icon': { ml: '4px', color: '#6366f1 !important' },
                        }}
                    />
                )}
            </Box>
        </Box>
    );
}

function SavedIdeaCard({ saved, onDelete }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const [deleting, setDeleting] = useState(false);
    const idea = saved.idea || {};
    const formatColor = FORMAT_COLORS[idea.format] || '#6366f1';

    const handleDelete = async () => {
        setDeleting(true);
        try { await onDelete(saved.id); } finally { setDeleting(false); }
    };

    return (
        <Box sx={{
            borderRadius: '10px',
            border: `1px solid ${alpha(formatColor, 0.2)}`,
            borderLeft: `3px solid ${formatColor}`,
            background: theme.palette.background.paper,
            overflow: 'hidden',
            opacity: deleting ? 0.5 : 1,
            pointerEvents: deleting ? 'none' : 'auto',
            transition: 'all 0.15s',
            '&:hover': { boxShadow: `0 4px 16px ${alpha(formatColor, 0.12)}` },
        }}>
            {/* Header row */}
            <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.3, mb: 0.4 }}>
                        {idea.title || 'Untitled'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                        {idea.format && (
                            <Chip label={idea.format} size="small" sx={{ bgcolor: alpha(formatColor, 0.1), color: formatColor, fontWeight: 700, height: 18, fontSize: '0.62rem', borderRadius: '5px' }} />
                        )}
                        {saved.source_topic && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
                                Topic: {saved.source_topic}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Tooltip title="Remove saved idea">
                    <IconButton size="small" onClick={handleDelete} disabled={deleting}
                        sx={{ color: '#f59e0b', '&:hover': { color: '#ef4444' }, flexShrink: 0 }}>
                        <BookmarkIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Hook */}
            {idea.hook && (
                <Box sx={{ px: 2, pb: 1 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        "{idea.hook}"
                    </Typography>
                </Box>
            )}

            {/* Key points */}
            {idea.key_points?.length > 0 && (
                <Box sx={{ px: 2, pb: 1.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                        {idea.key_points.slice(0, 2).map((pt, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.6 }}>
                                <BulletCheckIcon sx={{ fontSize: 11, color: formatColor, mt: '3px', flexShrink: 0 }} />
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.72rem', lineHeight: 1.4 }}>{pt}</Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Cloud save indicator */}
            <Box sx={{ px: 2, py: 0.75, bgcolor: alpha('#10b981', isDark ? 0.06 : 0.04), borderTop: `1px solid ${alpha('#10b981', 0.1)}`, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CloudIcon sx={{ fontSize: 11, color: '#10b981' }} />
                <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.65rem', fontWeight: 600 }}>Saved to your account</Typography>
            </Box>
        </Box>
    );
}

function HistoryCard({ entry, onRegenerate }) {
    const modeInfo = IDEA_MODES.find(m => m.value === entry.mode);
    const ModeIcon = MODE_ICONS[entry.mode] || AutoAwesomeIcon;
    return (
        <Box sx={{
            p: 1.75, borderRadius: '10px',
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex', gap: 1.5, alignItems: 'center',
        }}>
            <ModeIcon sx={{ color: '#6366f1', fontSize: 18, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {entry.topic || <em style={{ opacity: 0.5 }}>auto-topic</em>}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {modeInfo?.label || entry.mode} · {entry.idea_count} ideas
                    {entry.platform ? ` · ${entry.platform}` : ''}
                </Typography>
            </Box>
            {onRegenerate && (
                <Tooltip title="Regenerate">
                    <IconButton size="small" onClick={() => onRegenerate(entry)}
                        sx={{ color: '#6366f1' }}>
                        <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
}

class IdeaCardErrorBoundary extends React.Component {
    state = { hasError: false };
    static getDerivedStateFromError() { return { hasError: true }; }
    render() {
        if (this.state.hasError) {
            return (
                <Box sx={{ p: 2, borderRadius: '10px', border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Failed to render this idea.</Typography>
                </Box>
            );
        }
        return this.props.children;
    }
}

export default function IdeasResults({
    ideaResult, card,
    savedIdeas, onSaveIdea, onDeleteSaved,
    ideasHistory, onRegenerate,
    activeTab, onTabChange,
}) {
    const [copiedIdx, setCopiedIdx] = useState(null);
    const [snackMsg, setSnackMsg] = useState(null);
    const [localSavedIds, setLocalSavedIds] = useState(new Set());
    const prevResultRef = useRef(null);

    // Clear in-memory saved state whenever a new generation arrives
    // so old bookmarks from the previous batch don't bleed into the new one
    useEffect(() => {
        if (ideaResult && ideaResult !== prevResultRef.current) {
            prevResultRef.current = ideaResult;
            setLocalSavedIds(new Set());
        }
    }, [ideaResult]);
    const savedIds = useMemo(
        () => {
            const fromProps = new Set((savedIdeas || []).map(s => s.idea?.id).filter(Boolean));
            return new Set([...fromProps, ...localSavedIds]);
        },
        [savedIdeas, localSavedIds],
    );

    if (!ideaResult && !savedIdeas && !ideasHistory) return null;

    const ideas = ideaResult?.ideas || [];
    const selectedPlatform = ideaResult?.platform;
    const platformColor = PLATFORM_COLORS[selectedPlatform] || '#10b981';
    const PlatformIcon = PLATFORM_ICONS[selectedPlatform] || AllPlatformsIcon;
    const modeInfo = IDEA_MODES.find(m => m.value === ideaResult?.mode);
    const ModeIcon = MODE_ICONS[ideaResult?.mode] || AutoAwesomeIcon;

    const handleCopy = (idea, idx) => {
        const fullText = `${idea.title}\n\nHOOK: ${idea.hook}\n\n${idea.body}\n\nCTA: ${idea.cta}`;
        navigator.clipboard.writeText(fullText).then(() => {
            setCopiedIdx(idx);
            setSnackMsg('Full content strategy copied!');
            setTimeout(() => setCopiedIdx(null), 2000);
        });
    };

    const handleSave = (idea, idx) => {
        if (savedIds.has(idea.id)) return;
        setLocalSavedIds(prev => new Set([...prev, idea.id]));
        if (onSaveIdea) onSaveIdea(idea, ideaResult?.topic, ideaResult?.mode);
        setSnackMsg('Idea bookmarked!');
    };

    const tabs = ['Generate', 'Saved', 'History'].filter((t, i) => {
        if (i === 1) return Array.isArray(savedIdeas);
        if (i === 2) return Array.isArray(ideasHistory);
        return true;
    });
    const currentTab = activeTab || 'Generate';

    return (
        <Paper elevation={0} sx={card}>
            {/* Tab bar */}
            {tabs.length > 1 && (
                <Box role="tablist" aria-label="Ideas sections" sx={{ display: 'flex', gap: 0.5, mb: 2.5, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
                    {tabs.map(tab => (
                        <Chip
                            key={tab}
                            role="tab"
                            aria-selected={currentTab === tab}
                            aria-controls={`ideas-tabpanel-${tab.toLowerCase()}`}
                            label={tab === 'Saved' ? `Saved (${(savedIdeas || []).length})` : tab}
                            size="small"
                            onClick={() => onTabChange && onTabChange(tab)}
                            sx={{
                                bgcolor: currentTab === tab ? alpha('#6366f1', 0.12) : 'transparent',
                                color: currentTab === tab ? '#6366f1' : 'text.secondary',
                                fontWeight: currentTab === tab ? 700 : 500,
                                border: currentTab === tab ? `1px solid ${alpha('#6366f1', 0.3)}` : '1px solid transparent',
                                cursor: 'pointer',
                            }}
                        />
                    ))}
                </Box>
            )}

            {/* GENERATE TAB */}
            {currentTab === 'Generate' && (
                <>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.75 }}>
                        <Box sx={{
                            width: 36, height: 36, borderRadius: '10px',
                            bgcolor: alpha('#10b981', 0.1),
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <RocketIcon sx={{ color: '#10b981', fontSize: 20 }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                                Generated Strategy
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {ideaResult?.topic ? `for "${ideaResult.topic}"` : 'from your profile'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {/* Mode badge */}
                            {modeInfo && (
                                <Chip
                                    icon={<ModeIcon sx={{ fontSize: '12px !important' }} />}
                                    label={modeInfo.label}
                                    size="small"
                                    sx={{
                                        bgcolor: alpha('#6366f1', 0.08),
                                        color: '#6366f1',
                                        border: `1px solid ${alpha('#6366f1', 0.2)}`,
                                        fontWeight: 700, fontSize: '0.65rem',
                                        '& .MuiChip-icon': { ml: '5px', color: '#6366f1 !important' },
                                    }}
                                />
                            )}
                            {/* Personalized badge */}
                            {ideaResult?.personalized && (
                                <Tooltip title="Personalized to your Content DNA">
                                    <Chip
                                        icon={<PsychologyIcon sx={{ fontSize: '12px !important' }} />}
                                        label="Personalized"
                                        size="small"
                                        sx={{
                                            bgcolor: alpha('#10b981', 0.08),
                                            color: '#10b981',
                                            border: `1px solid ${alpha('#10b981', 0.2)}`,
                                            fontWeight: 700, fontSize: '0.65rem',
                                            '& .MuiChip-icon': { ml: '5px', color: '#10b981 !important' },
                                        }}
                                    />
                                </Tooltip>
                            )}
                            <Box sx={{
                                px: 1.5, py: 0.5, borderRadius: '8px',
                                bgcolor: alpha('#10b981', 0.08),
                                border: `1px solid ${alpha('#10b981', 0.2)}`,
                                display: 'flex', alignItems: 'center', gap: 0.75,
                            }}>
                                <AutoAwesomeIcon sx={{ fontSize: 14, color: '#10b981' }} />
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#10b981' }}>
                                    {ideas.length} ideas
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Optimized for
                        </Typography>
                        {selectedPlatform ? (
                            <Chip
                                icon={<PlatformIcon sx={{ fontSize: '14px !important', color: `${platformColor} !important` }} />}
                                label={selectedPlatform} size="small"
                                sx={{
                                    bgcolor: alpha(platformColor, 0.1), color: platformColor,
                                    border: `1px solid ${alpha(platformColor, 0.3)}`, fontWeight: 700,
                                    fontSize: '0.72rem', borderRadius: '6px',
                                    '& .MuiChip-icon': { ml: '5px' },
                                }}
                            />
                        ) : (
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                all platforms.
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {ideas.map((idea, i) => (
                            <IdeaCardErrorBoundary key={idea.id}>
                                <IdeaCard
                                    idea={idea}
                                    index={i}
                                    onCopy={handleCopy}
                                    onSave={handleSave}
                                    copiedIdx={copiedIdx}
                                    savedIds={savedIds}
                                />
                            </IdeaCardErrorBoundary>
                        ))}
                    </Box>
                </>
            )}

            {/* SAVED TAB */}
            {currentTab === 'Saved' && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>Saved Ideas</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                <CloudIcon sx={{ fontSize: 12, color: '#10b981' }} />
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
                                    Synced to your account — accessible from any device
                                </Typography>
                            </Box>
                        </Box>
                        <Chip
                            label={`${(savedIdeas || []).length} saved`}
                            size="small"
                            sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b', fontWeight: 700, fontSize: '0.68rem', border: `1px solid ${alpha('#f59e0b', 0.25)}` }}
                        />
                    </Box>
                    {(savedIdeas || []).length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 5 }}>
                            <BookmarkBorderIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1.5 }} />
                            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5 }}>
                                No saved ideas yet
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                                Tap the bookmark icon on any idea card to save it here permanently.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {savedIdeas.map(s => (
                                <SavedIdeaCard key={s.id} saved={s} onDelete={onDeleteSaved} />
                            ))}
                        </Box>
                    )}
                </>
            )}

            {/* HISTORY TAB */}
            {currentTab === 'History' && (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>Generation History</Typography>
                    {(ideasHistory || []).length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                            No history yet. Generate some ideas to see them here.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {ideasHistory.map(entry => (
                                <HistoryCard key={entry.id} entry={entry} onRegenerate={onRegenerate} />
                            ))}
                        </Box>
                    )}
                </>
            )}

            <Snackbar
                open={Boolean(snackMsg)} autoHideDuration={2000}
                onClose={() => setSnackMsg(null)}
                message={snackMsg}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </Paper>
    );
}
